import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Filter, Loader2, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/billing/StatusBadge";
import { db, formatDate } from "@/lib/billing";
import { useCompany } from "@/hooks/useCompany";

type DbError = { message: string };
type DbResult<T = unknown> = { data: T | null; error: DbError | null };
type LooseQuery<T = unknown> = PromiseLike<DbResult<T>> & {
  select: (columns?: string) => LooseQuery<T>;
  order: (column: string, options?: { ascending?: boolean }) => LooseQuery<T>;
  limit: (count: number) => LooseQuery<T>;
  eq: (column: string, value: unknown) => LooseQuery<T>;
};
type LooseDb = { from: (table: string) => LooseQuery };
type AuditLogRow = {
  id: string;
  company_id: string | null;
  user_id?: string | null;
  actor_id?: string | null;
  actor_user_id?: string | null;
  action?: string | null;
  event_action?: string | null;
  entity_type?: string | null;
  resource_type?: string | null;
  entity_id?: string | null;
  message?: string | null;
  metadata?: unknown;
  status?: string | null;
  created_at: string;
  companies?: { name?: string | null; slug?: string | null } | null;
};

const auditDb = db as unknown as LooseDb;

async function fetchAuditLogs(companyId: string | null, isPlatformOwner: boolean): Promise<AuditLogRow[]> {
  let query = auditDb
    .from("audit_logs")
    .select("*, companies(name,slug)")
    .order("created_at", { ascending: false })
    .limit(500);
  if (companyId && !isPlatformOwner) query = query.eq("company_id", companyId);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as AuditLogRow[];
}

export default function AdminAuditLogsPage() {
  const { currentCompanyId, isPlatformOwner, isLoading: companyLoading } = useCompany();
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["admin-audit-logs", currentCompanyId, isPlatformOwner],
    queryFn: () => fetchAuditLogs(currentCompanyId, isPlatformOwner),
    enabled: !companyLoading,
  });

  const companies = useMemo(() => Array.from(new Map(logs.map((log) => [log.company_id, log.companies?.name ?? "Unknown"])).entries()).filter(([id]) => id), [logs]);
  const actions = useMemo(() => Array.from(new Set(logs.map((log) => log.action ?? log.event_action).filter(Boolean))) as string[], [logs]);
  const entities = useMemo(() => Array.from(new Set(logs.map((log) => log.entity_type ?? log.resource_type).filter(Boolean))) as string[], [logs]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return logs.filter((log) => {
      const created = new Date(log.created_at).getTime();
      const content = [log.action, log.event_action, log.entity_type, log.resource_type, log.entity_id, log.message, log.companies?.name, JSON.stringify(log.metadata ?? {})].join(" ").toLowerCase();
      return (!term || content.includes(term))
        && (companyFilter === "all" || log.company_id === companyFilter)
        && (actionFilter === "all" || (log.action ?? log.event_action) === actionFilter)
        && (entityFilter === "all" || (log.entity_type ?? log.resource_type) === entityFilter)
        && (statusFilter === "all" || (log.status ?? "success") === statusFilter)
        && (!fromDate || created >= new Date(fromDate).getTime())
        && (!toDate || created <= new Date(toDate).getTime() + 86400000);
    });
  }, [actionFilter, companyFilter, entityFilter, fromDate, logs, search, statusFilter, toDate]);

  if (isLoading || companyLoading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Audit Logs</h1>
          <p className="text-xs text-muted-foreground">Filterable company timeline for websites, articles, integrations, members, and automation.</p>
        </div>
      </header>

      <main className="space-y-6 p-6">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Filter className="h-4 w-4" />Filters</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-4">
            <Field label="Search">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Action, entity, company, metadata" />
              </div>
            </Field>
            {isPlatformOwner && (
              <Field label="Company">
                <Select value={companyFilter} onValueChange={setCompanyFilter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All companies</SelectItem>
                    {companies.map(([id, name]) => <SelectItem key={String(id)} value={String(id)}>{String(name)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Action">
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All actions</SelectItem>{actions.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Entity">
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All entities</SelectItem>{entities.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["all", "success", "failed", "warning", "info", "active", "ended"].map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="From"><Input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} /></Field>
            <Field label="To"><Input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} /></Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Timeline ({filtered.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filtered.length === 0 && <div className="py-12 text-center text-sm text-muted-foreground">No audit entries match this filter.</div>}
              {filtered.map((log) => (
                <div key={log.id} className="rounded-lg border border-border p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-foreground">{log.action ?? log.event_action}</span>
                        <span className="text-sm text-muted-foreground">on</span>
                        <span className="text-sm font-medium text-foreground">{log.entity_type ?? log.resource_type ?? "record"}</span>
                        <StatusBadge status={log.status ?? "success"} />
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {formatDate(log.created_at)} · {log.companies?.name ?? "No company"} · actor {log.user_id ?? log.actor_id ?? log.actor_user_id ?? "system"}
                      </p>
                    </div>
                    <span className="max-w-[220px] truncate text-xs text-muted-foreground">{log.entity_id}</span>
                  </div>
                  {log.metadata && (
                    <pre className="mt-3 max-h-32 overflow-auto rounded-md bg-muted p-3 text-[11px] text-muted-foreground">
                      {JSON.stringify(log.metadata, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}</div>;
}
